<template>
  <component
    :is="props.useClientLayout ? AdminLayout : PortalLayout"
    section="Reports"
    title="Training Report"
    hideHeader
  >
    <div class="pb-4">
      <BackButton to=".."> Back to Reports List </BackButton>
    </div>
    <h1 class="py-3 mb-6 border-b flex">
      <div class="text-xl">
        <div class="font-bold">Training Report</div>
        <div>
          {{ user.isTenantAdmin ? user.tenantName : property?.name }}
        </div>
      </div>
    </h1>
    <Loading v-if="isPropertyLoading" />
    <div v-else>
      <DateRange @change="dateRangeChange" :start="startDate" :end="endDate" />

      <Loading v-if="isReportLoading" />
      <div v-else-if="trainingResults?.results?.length === 0">
        No training results found
      </div>
      <div v-else class="report-container">
        <div class="flex">
          <div class="flex flex-row gap-2 grow">
            <div v-if="!user.isTenantAdmin" class="basis-1/5">
              <BaseSelect
                defaultText="All Tenants"
                class="px-4 mb-1 text-sm bg-white"
                v-model="filteredTenant"
                :options="tenants"
              />
            </div>
            <div class="basis-1/5">
              <BaseSelect
                defaultText="All Roles"
                class="px-4 mb-1 text-sm bg-white"
                v-model="filteredRole"
                :options="siteRoles"
              />
            </div>
            <div class="basis-1/5">
              <BaseSelect
                v-if="property.floorList?.length > 0"
                defaultText="All Floors"
                class="px-4 mb-1 text-sm bg-white"
                v-model="filteredFloor"
                :options="property.floorList"
              />
            </div>
            <div class="basis-1/6">
              <BaseCheckbox
                label="Show only passing scores"
                v-model="showOnlyPassingScores"
                class="my-4"
              />
            </div>
            <div v-if="!props.useClientLayout" class="basis-1/6">
              <BaseCheckbox
                label="Exclude Test Accounts"
                v-model="excludeTestAccounts"
                class="my-4"
              />
            </div>
          </div>
          <BaseButton class="float-right my-1 text-sm ml-2" @click="downloadExcel">
            Export to Excel
          </BaseButton>
        </div>
        <table class="table-auto w-full mt-4 border-2 shadow text-sm">
          <thead class="font-sm font-bold">
            <tr>
              <td
                class="pl-3 py-2 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Date)"
              >
                Date
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Date"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                class="pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.FirstName)"
              >
                <span class="xl:inline hidden">First </span>Name
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.FirstName"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                class="xl:table-cell hidden pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.LastName)"
              >
                Last Name
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.LastName"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                v-if="filteredTenant == null || filteredTenant.length == 0"
                class="pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Tenant)"
              >
                Tenant
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Tenant"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                v-if="filteredRole == null || filteredRole.length == 0"
                class="pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Role)"
              >
                Role
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Role"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                v-if="filteredFloor == null || filteredFloor.length == 0"
                class="pl-3 cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Floor)"
              >
                Floor
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Floor"
                  :isUp="!isSortingDown"
                />
              </td>
              <td class="pl-3 cursor-pointer" @click="ChangeSort(ColumnsToSort.Module)">
                Module <span class="inline md:hidden"> / Test </span>
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Module"
                  :isUp="!isSortingDown"
                />
              </td>
              <td
                class="pl-3 hidden md:table-cell cursor-pointer"
                @click="ChangeSort(ColumnsToSort.Test)"
              >
                Test
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Test"
                  :isUp="!isSortingDown"
                />
              </td>
              <td class="px-3 cursor-pointer" @click="ChangeSort(ColumnsToSort.Score)">
                Score
                <UpDownArrow
                  class="float-right"
                  v-if="columnToSort == ColumnsToSort.Score"
                  :isUp="!isSortingDown"
                />
              </td>
            </tr>
          </thead>
          <tr
            v-for="(trainee, i) in filteredTrainingResults"
            :key="i"
            class="font-xs font-mono hover:border-sky-800 border-bottom-2 hover:bg-sky-300"
            :class="i % 2 ? '' : 'bg-stone-200'"
          >
            <td class="pl-3">{{ mmddyyyy(trainee.dateCompleted) }}</td>
            <td class="pl-3">
              {{ trainee.firstName }}
              <span class="inline xl:hidden">{{ trainee.lastName }}</span>
            </td>
            <td class="xl:table-cell hidden pl-3">{{ trainee.lastName }}</td>
            <td v-if="filteredTenant == null || filteredTenant.length == 0" class="pl-3">
              {{ trainee.tenantName }}
            </td>
            <td v-if="filteredRole == null || filteredRole.length == 0" class="pl-3">
              {{ trainee.roleName }}
              <span
                v-if="getSiteRoleById(trainee.siteRoleId)?.isStaffRole"
                class="text-gray-500"
              >
                (Staff)
              </span>
            </td>
            <td v-if="filteredFloor == null || filteredFloor.length == 0" class="pl-3">
              {{ trainee.floor }}
            </td>
            <td class="text-xs md:text-sm pl-3">
              {{ trainee.moduleName }}
              <span class="md:hidden inline font-bold text-xs">
                <br />
                / {{ trainee.testName }}
              </span>
            </td>
            <td class="md:table-cell hidden pl-3">{{ trainee.testName }}</td>
            <td class="px-3">
              {{ trainee.correctAnswerCount }}/{{ trainee.answerCount }}
            </td>
          </tr>
        </table>
        <div>
          <BaseButton class="float-right my-1 text-sm ml-2" @click="downloadExcel">
            Export to Excel
          </BaseButton>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { IContent, IJsonSheet } from "json-as-xlsx";
import * as jsonAsXlsx from "json-as-xlsx";
import PortalLayout from "../../layouts/PortalLayout.vue";
import AdminLayout from "../../layouts/AdminLayout.vue";
import { useBldgStore } from "../../store/bldgStore";
import { TrainingResultVm, TrainingResultDTO } from "../../store/generatedFetchClient";
import { useReportStore } from "../../store/reportStore";
import { NameValue } from "../../sharedTypes";
import { useUserStore } from "../../store/userStore";

const props = withDefaults(defineProps<{ useClientLayout?: boolean }>(), {
  useClientLayout: false,
});

const bldgStore = useBldgStore();
const reportStore = useReportStore();
const userStore = useUserStore();
var isPropertyLoading = computed(() => bldgStore.isLoading);
var isReportLoading = ref(false);

var property = computed(() => bldgStore.property);
var user = computed(() => userStore.user);
var tenants = computed<string[]>(() => property.value.tenants.map((t) => t.name));
var siteRoles = computed<NameValue[]>(() =>
  property.value.siteRoles.map(
    (sr) =>
      ({
        name: sr.roleAlias + (sr.isStaffRole ? " (Staff)" : ""),
        value: sr.siteRoleId,
      } as NameValue)
  )
);

var trainingResults = computed<TrainingResultVm>(() => reportStore.trainingResults);

var filteredTrainingResults = computed<TrainingResultDTO[]>(() => {
  let results = trainingResults.value?.results?.slice() ?? [];

  if (filteredTenant.value != null && filteredTenant.value.length > 0)
    results = results.filter((result) => result.tenantName == filteredTenant.value);
  if (filteredRole.value != null && filteredRole.value.length > 0)
    results = results.filter(
      (result) => result.siteRoleId == parseInt(filteredRole.value)
    );
  if (filteredFloor.value != null && filteredFloor.value.length > 0)
    results = results.filter((result) => result.floor == filteredFloor.value);
  if (showOnlyPassingScores.value)
    results = results.filter(
      (result) =>
        result.correctAnswerCount / result.answerCount >=
        property.value.minimumScorePasses / 100
    );

  switch (columnToSort.value) {
    case ColumnsToSort.Date:
      results = results.sort(
        (a, b) =>
          new Date(a.dateCompleted).getTime() - new Date(b.dateCompleted).getTime()
      );
      break;
    case ColumnsToSort.FirstName:
      results = results.sort((a, b) => {
        if (a.firstName == b.firstName) {
          return (a.lastName ?? "").localeCompare(b.lastName ?? "");
        }
        return (a.firstName ?? "").localeCompare(b.firstName ?? "");
      });
      break;
    case ColumnsToSort.LastName:
      results = results.sort((a, b) => {
        if (a.lastName == b.lastName) {
          return (a.firstName ?? "").localeCompare(b.firstName ?? "");
        }
        return (a.lastName ?? "").localeCompare(b.lastName ?? "");
      });
      break;
    case ColumnsToSort.Tenant:
      results = results.sort((a, b) =>
        (a.tenantName ?? "").localeCompare(b.tenantName ?? "")
      );
      break;
    case ColumnsToSort.Role:
      results = results.sort((a, b) =>
        (a.roleName ?? "").localeCompare(b.roleName ?? "")
      );
      break;
    case ColumnsToSort.Floor:
      results = results.sort(
        (a, b) =>
          property.value.floorList.findIndex(
            (i) => i.toLocaleLowerCase() == b.floor.toLocaleLowerCase()
          ) -
          property.value.floorList.findIndex(
            (i) => i.toLocaleLowerCase() == a.floor.toLocaleLowerCase()
          )
      );
      break;
    case ColumnsToSort.Module:
      results = results.sort((a, b) => {
        if (a.moduleName == b.moduleName) {
          return (a.testName ?? "").localeCompare(b.testName ?? "");
        }
        return (a.moduleName ?? "").localeCompare(b.moduleName ?? "");
      });
      break;
    case ColumnsToSort.Test:
      results = results.sort((a, b) => {
        if (a.testName == b.testName) {
          return (a.moduleName ?? "").localeCompare(b.moduleName ?? "");
        }
        return (a.testName ?? "").localeCompare(b.testName ?? "");
      });
      break;
    case ColumnsToSort.Score:
      results = results.sort(
        (a, b) =>
          a.correctAnswerCount / a.answerCount - b.correctAnswerCount / b.answerCount
      );
      break;
  }

  if (isSortingDown.value) {
    results = results.reverse();
  }

  console.log(` TR> Filtered Training Results:  ${results.length} records.`);

  return results;
});

//Set initial values from query params (if provided) or last-used range saved in reportStore
const route = useRoute();
let startDate = ref(route.query.start ? new Date(route.query.start as string) : reportStore.rangeStart);
let endDate = ref(route.query.end ? new Date(route.query.end as string) : reportStore.rangeEnd);

//Filters
var filteredTenant = ref<string>("");
var filteredRole = ref<string>("");
var filteredFloor = ref<string>("");
var showOnlyPassingScores = ref<boolean>(false);

let excludeTestAccounts = ref(false);

enum ColumnsToSort {
  Date,
  FirstName,
  LastName,
  Tenant,
  Role,
  Floor,
  Module,
  Test,
  Score,
}

var columnToSort = ref<ColumnsToSort>(ColumnsToSort.Date);
var isSortingDown = ref<boolean>(true);

function ChangeSort(column: ColumnsToSort) {
  console.log(" TR> Changing sort to " + column);
  if (column == columnToSort.value) {
    isSortingDown.value = !isSortingDown.value;
  } else {
    columnToSort.value = column;
    isSortingDown.value = true;
  }
}

onMounted(loadData);
watch(property, loadData);
watch(excludeTestAccounts, loadData);

async function loadData() {
  console.log(" TR> Loading training report data");
  isReportLoading.value = true;
  if (property?.value?.portalId) {
    if (props.useClientLayout) excludeTestAccounts.value = true;
    await reportStore.getTrainingReport(
      property.value.portalId,
      startDate.value,
      endDate.value,
      excludeTestAccounts.value
    );
  }

  if (user.value.isTenantAdmin) {
    filteredTenant.value = user.value.tenantName;
  }

  isReportLoading.value = false;
}

function dateRangeChange(range: { start: Date; end: Date }) {
  console.log(`Date range changed `, range);
  startDate.value = range.start;
  endDate.value = range.end;
  loadData();
}

function mmddyyyy(d: Date | null | undefined | string): string {
  if (d !== null && d !== undefined) {
    if (typeof d == "string") {
      return new Date(d).toLocaleDateString();
    }
    if (d.toLocaleDateString !== undefined) {
      return d.toLocaleDateString();
    }
  }
  return "";
}

function downloadExcel() {
  try {
    console.log("Generating Excel file for training results", trainingResults.value);

    // Extract the xlsx function from the namespace import
    const xlsx = (jsonAsXlsx as any).default || (jsonAsXlsx as any).xlsx || jsonAsXlsx;

    let data: IJsonSheet[] = [
      {
        sheet: "Report Summary",
        columns: [
          { label: "Total to Date Report for " + property.value.name, value: "A" },
          { label: "          ", value: "B" },
          { label: "           ", value: "C" }, //Keep different label lengths, matching label strings breaks the table
        ],
        content: [
          {
            A: "Date Range",
            B: startDate.value.toLocaleDateString("en-US", {
              month: "numeric",
              day: "numeric",
              year: "numeric",
            }),
            C: endDate.value.toLocaleDateString("en-US", {
              month: "numeric",
              day: "numeric",
              year: "numeric",
            }),
          },
          {
            A: "",
            B: "",
            C: "",
          },
          {
            A: "Training Program",
            B: "Registered Users",
            C: "Completed Training",
          },
        ],
      },
    ];

    let combinedSheet: IJsonSheet = {
      sheet: "Combined Results",
      columns: [
        { label: "Date", value: "date" },
        { label: "Last Name", value: "lastName" },
        { label: "First Name", value: "firstName" },
        { label: "Tenant", value: "tenant" },
        { label: "Role", value: "role" },
        { label: "Floor", value: "floor" },
        { label: "Module", value: "module" },
        { label: "Score", value: "score" },
        { label: "Name of Test", value: "testName" },
      ],
      content: [] as IContent[],
    };

    let totalCompletedTraining: number = 0;

    for (let key in trainingResults.value.userCounts) {
      key = key.replace(/[^a-zA-Z0-9]/g, "");
      if (key == "Total") continue;

      let moduleResults = trainingResults.value.results.filter(
        (r) => r.trainingProgramName?.replace(/[^a-zA-Z0-9]/g, "") == key
      );

      let sheet: IJsonSheet = {
        sheet: key,
        columns: [
          { label: "Date", value: "date" },
          { label: "Last Name", value: "lastName" },
          { label: "First Name", value: "firstName" },
          { label: "Tenant", value: "tenant" },
          { label: "Role", value: "role" },
          { label: "Floor", value: "floor" },
          { label: "Module", value: "module" },
          { label: "Score", value: "score" },
          { label: "Name of Test", value: "testName" },
        ],
        content: [],
      };

      let programCompletedTraining: number = 0;

      moduleResults.forEach((result) => {
        let newRow = {
          date: new Date(result.dateCompleted).toLocaleDateString("en-US", {
            month: "numeric",
            day: "numeric",
            year: "numeric",
          }),
          lastName: result.lastName,
          firstName: result.firstName,
          tenant: result.tenantName,
          role: result.roleName,
          floor: result.floor,
          module: result.moduleName,
          score: result.correctAnswerCount + " of " + result.answerCount,
          testName: result.testName,
        };
        sheet.content.push(newRow);
        combinedSheet.content.push(newRow);

        totalCompletedTraining++;
        programCompletedTraining++;
      });

      data[0].content.push({
        A: key,
        B: trainingResults.value.userCounts[key],
        C: programCompletedTraining,
      });
      data.push(sheet);
    }

    data[0].content.push({
      A: "Totals",
      B: trainingResults.value.userCounts["Total"],
      C: totalCompletedTraining,
    });
    data.splice(1, 0, combinedSheet);

    console.log("generating Excel file", data);
    let fileName =
      "AK_CompletedTests_" + property.value.name + "_" + new Date().toLocaleDateString();
    fileName = fileName.replaceAll(" ", "_");
    fileName = fileName.replaceAll("/", "-");

    console.log("generating Excel file: " + fileName);
    xlsx(data, { fileName: fileName });
  } catch (error: any) {
    console.error("Error generating Excel file: ", error);
  }
}

// #region Helpers

var getSiteRoleById = (siteRoleId: number) =>
  property.value?.siteRoles?.find((r) => r.siteRoleId === siteRoleId);

// #endregion Helpers
</script>
